import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { IconArrowLeft, IconMail, IconCheck } from "@tabler/icons-react";
import axios from "axios";
import { useI18n } from "@/contexts/I18nContext";
import { AuthShell } from "@/pages/auth/Login";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const { lang } = useI18n();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await axios.post(`${BACKEND}/api/auth/password/forgot`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      // The endpoint is enumeration-safe — errors here are network/format.
      const msg = err?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : (lang === "es" ? "No se pudo procesar tu solicitud. Intentá de nuevo." : "Could not process your request."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <p className="eyebrow">Chiessa Institute</p>
      <h1 className="font-display font-medium text-[30px] mt-2 leading-tight">
        {lang === "es" ? "Olvidé mi contraseña" : "Forgot password"}
      </h1>
      <p className="text-muted mt-2 text-[13.5px] leading-relaxed">
        {lang === "es"
          ? "Escribí el correo de tu cuenta y te enviamos un enlace para restablecer tu contraseña."
          : "Enter your account email and we'll send a reset link."}
      </p>

      {sent ? (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 card border-l-[3px] border-l-teal bg-teal-soft/40"
          data-testid="forgot-sent"
        >
          <div className="flex items-start gap-2 text-teal-deep">
            <IconCheck size={16} className="mt-0.5" />
            <div>
              <p className="text-[14px] font-medium">
                {lang === "es" ? "¡Listo! Revisá tu bandeja." : "Done! Check your inbox."}
              </p>
              <p className="text-[12.5px] text-muted mt-2 leading-relaxed">
                {lang === "es"
                  ? "Si existe una cuenta con ese correo, te enviamos instrucciones para restablecerla. Revisá también la carpeta de spam. El enlace vence en 2 horas y sirve solo una vez."
                  : "If an account exists for that email, instructions were sent. Check spam too. The link expires in 2 hours and works only once."}
              </p>
            </div>
          </div>
          <Link to="/login" className="btn btn-ghost mt-4 text-[13px]" data-testid="forgot-back-to-login">
            <IconArrowLeft size={13} /> {lang === "es" ? "Volver a iniciar sesión" : "Back to sign in"}
          </Link>
        </motion.div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="field">
            <label>{lang === "es" ? "Correo" : "Email"}</label>
            <div className="relative">
              <IconMail size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-tert" />
              <input
                className="input pl-8"
                type="email"
                required
                autoFocus
                placeholder="tu@correo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email"
              />
            </div>
          </div>
          {error && <p className="text-errtext text-[13px]" data-testid="forgot-error">{error}</p>}
          <button type="submit" className="btn w-full" disabled={loading || !email.trim()} data-testid="forgot-submit">
            {loading ? (lang === "es" ? "Enviando..." : "Sending...") : (lang === "es" ? "Enviar enlace" : "Send link")}
          </button>
          <div className="flex justify-between text-[12.5px] text-muted mt-3">
            <Link to="/login" data-testid="forgot-back-link">
              <IconArrowLeft size={11} className="inline mr-1" />
              {lang === "es" ? "Volver a iniciar sesión" : "Back to sign in"}
            </Link>
          </div>
        </form>
      )}
    </AuthShell>
  );
}
